import Vector from "../math/Vector";
import { Settings } from '../../Settings';
import GridZone from "./GridZone";
import GridBox from "./GridBox";
import Grid from "./Grid";
import GridTile from "./GridTile";
import WorldGrid from "./WorldGrid";
import Fast from "../../util/Fast";

export default class GridRegion extends GridBox {
    /**
     * @param {GridZone} zone
     * @param {Vector} origin
     */
    constructor(zone, origin) {
        super(origin);

        /**
         * @private
         * @constant
         * @type {GridZone}
         */
        this.zone = zone;

        /**
         * @type {Array.<GridTile>}
         */
        this.tiles = this.createTiles();

        /**
         * @type {Array.<GridTile>}
         */
        this.mineTiles = [];

        /**
         * @type {number}
         */
        this.vectorFieldNumber = -1;
    }

    /**
     * @private
     * @returns {Array.<GridTile>}
     */
    createTiles() {

        const tiles = new Array(Settings.grid.numTileColsInRegion * Settings.grid.numTileRowsInRegion);

        const grid = this.zone.grid;

        const origin = this.getOrigin();
        const x = origin.x;
        const y = origin.y;

        let i = 0;
        for (let col = 0; col < Settings.grid.numTileColsInRegion; col++) {
            for (let row = 0; row < Settings.grid.numTileRowsInRegion; row++) {
                tiles[i++] = new GridTile(grid, this, new Vector(x + (Settings.tile.size * col), y + (Settings.tile.size * row)));
            }
        }

        return tiles;
    }

    /**
     * Invoked when the owner grid is assigned a new location.
     * @param {number} originX 
     * @param {number} originY 
     */
    initialize(originX, originY) {
        this.origin.set(originX, originY);

        const x = this.origin.x;
        const y = this.origin.y;

        let i = 0;
        for (let col = 0; col < Settings.grid.numTileColsInRegion; col++) {
            for (let row = 0; row < Settings.grid.numTileRowsInRegion; row++) {
                //const index = (col * Settings.grid.numTileRowsInRegion) + row;
                const tile = this.tiles[i++];
                tile.initialize(x + (Settings.tile.size * col), y + (Settings.tile.size * row));
            }
        }

        this.mineTiles.length = 0;
        this.vectorFieldNumber = -1;
    }

    /**
     * @param {GridTile} tile 
     * @returns {number}
     */
    getTileIndex(tile) {
        const col = tile.getTileCol() - this.getOriginTileCol();
        const row = tile.getTileRow() - this.getOriginTileRow();
        return (col * Settings.grid.numTileRowsInRegion) + row;
    }
    
    /**
     * @returns {GridZone}
     */
    getZone() {
        return this.zone;
    }

    /**
     * @returns {Grid}
     */
    getGrid() { return this.zone.grid; }

    /**
     * @returns {WorldGrid}
     */
    getWorldGrid() {
        return this.zone.getWorldGrid();
    }

    /**
     * @returns {Array.<GridTile>}
     */
    getTiles() {
        return this.tiles;
    }

    /**
     * @returns {number}
     */
    getPixelWidth() {
        return Settings.grid.regionPixelWidth;
    }

    /**
     * @returns {number}
     */
    getPixelHeight() {
        return Settings.grid.regionPixelHeight;
    }


    /**
     * @param {Vector} pixelLocation 
     * @returns {GridTile}
     */
    findGridTile(pixelLocation) {
        // final int px = (int) pixelLocation.x;
        // final int py = (int) pixelLocation.y;

        const tileCol = Fast.floor(pixelLocation.x / Settings.tile.size);
        const tileRow = Fast.floor(pixelLocation.y / Settings.tile.size);

        return this.getGridTileAbsolute(tileCol, tileRow);
    }

    /**
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {GridTile}
     */
    getGridTileAbsolute(tileCol, tileRow) {
        const origin = this.origin;
        const tileOriginCol = Fast.floor(origin.x / Settings.tile.size);
        const tileOriginRow = Fast.floor(origin.y / Settings.tile.size);

        return this.getGridTileLocal(tileCol - tileOriginCol, tileRow - tileOriginRow);
    }

    /**
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {GridTile}
     */
    getGridTileLocal(tileCol, tileRow) {
        if (tileCol < 0 || tileCol >= Settings.grid.numTileColsInRegion) {
            return null;
        }
        if (tileRow < 0 || tileRow >= Settings.grid.numTileRowsInRegion) {
            return null;
        }

        const index = (tileCol * Settings.grid.numTileRowsInRegion) + tileRow;
        if (index >= 0 && index < this.tiles.length) {
            return this.tiles[index];
        }
        else {
            console.log("GridRegion.getGridTileLocal() Failed to find tile. col=" + tileCol + " row=" + tileRow + " index=" + index);
            return null;
        }
    }

    // The tiles that are internal to a region (ie. not on the edge) only need to
    // have their neighbors found once.
    /**
     */
    findTileNeighborsFull() {
        for (let i = 0; i < this.tiles.length; i++) {
            this.tiles[i].findNeighbors();
        }
    }

    /**
     * @param {number} col 
     * @param {number} row 
     * @returns {GridTile}
     */
    getTile(col, row) {
        return this.tiles[(col * Settings.grid.numTileRowsInRegion) + row];
    }

    findRegionBorderTileNeighbors() {
        // The origin is at the bottom left corner
        // South row is 0, North is num rows - 1
        const rightCol = Settings.grid.numTileColsInRegion - 1;
        const topRow = Settings.grid.numTileRowsInRegion - 1;
        const bottomRow = 0;
        const leftCol = 0;

        this.getTile(rightCol, topRow).findNeighbors();   // top right
        this.getTile(rightCol, bottomRow).findNeighbors();   // bottom right
        this.getTile(leftCol, topRow).findNeighbors();   // top left
        this.getTile(leftCol, bottomRow).findNeighbors();   // borrom left
        
        // East, minus the corners
        for (let row = 1; row < Settings.grid.numTileRowsInRegion - 1; row++) {
            this.getTile(rightCol, row).findNeighbors();
        }

        // West, excluding corners
        for (let row = 1; row < Settings.grid.numTileRowsInRegion - 1; row++) {
            this.getTile(leftCol, row).findNeighbors();
        }

        // South, excluding corners
        for (let col = 1; col < Settings.grid.numTileColsInRegion - 1; col++) {
            this.getTile(col, bottomRow).findNeighbors();
        }

        // North, excluding corners
        for (let col = 1; col < Settings.grid.numTileColsInRegion - 1; col++) {
            this.getTile(col, topRow).findNeighbors();
        }
    }

    findTileNeighborsBorderRight() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numTileColsInRegion - 1;
        for (let row = 0; row < Settings.grid.numTileRowsInRegion; row++) {
            this.getTile(eastCol, row).findNeighborRight();
        }
    }
    nullifyTileNeighborsBorderRight() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numTileColsInRegion - 1;
        for (let row = 0; row < Settings.grid.numTileRowsInRegion; row++) {
            this.getTile(eastCol, row).nullifyNeighborRight();
        }
    }

    evaluateTileBordersRight() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numTileColsInRegion - 1;
        for (let row = 0; row < Settings.grid.numTileRowsInRegion; row++) {
            this.getTile(eastCol, row).evaluateBorders();
        }
    }

    findTileNeighborsBorderLeft() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numTileRowsInRegion; row++) {
            this.getTile(0, row).findNeighborLeft();
        }
    }
    nullifyTileNeighborsBorderLeft() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numTileRowsInRegion; row++) {
            this.getTile(0, row).nullifyNeighborLeft();
        }
    }

    evaluateTileBordersLeft() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numTileRowsInRegion; row++) {
            this.getTile(0, row).evaluateBorders();
        }
    }

    findTileNeighborsBorderBottom() {
        const southRow = Settings.grid.numTileRowsInRegion - 1;
        for (let col = 0; col < Settings.grid.numTileColsInRegion; col++) {
            this.getTile(col, southRow).findNeighborBottom();
        }
    }
    nullifyTileNeighborsBorderBottom() {
        const southRow = Settings.grid.numTileRowsInRegion - 1;
        for (let col = 0; col < Settings.grid.numTileColsInRegion; col++) {
            this.getTile(col, southRow).nullifyNeighborBottom();
        }
    }

    evaluateTileBordersBottom() {
        const southRow = Settings.grid.numTileRowsInRegion - 1;
        for (let col = 0; col < Settings.grid.numTileColsInRegion; col++) {
            this.getTile(col, southRow).evaluateBorders();
        }
    }

    findTileNeighborsBorderTop() {
        for (let col = 0; col < Settings.grid.numTileColsInRegion; col++) {
            this.getTile(col, 0).findNeighborTop();
        }
    }
    nullifyTileNeighborsBorderTop() {
        for (let col = 0; col < Settings.grid.numTileColsInRegion; col++) {
            this.getTile(col, 0).nullifyNeighborTop();
        }
    }

    evaluateTileBordersTop() {
        for (let col = 0; col < Settings.grid.numTileColsInRegion; col++) {
            this.getTile(col, 0).evaluateBorders();
        }
    }

}
